import fetch from "isomorphic-fetch";
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";

// Retries transient network failures (e.g. "Premature close" from api.swapcard.com).
// Without this, a single dropped connection during the Gatsby build crashes the whole deploy.
const retryLink = new RetryLink({
  delay: { initial: 500, max: 5000, jitter: true },
  attempts: {
    max: 5,
    // Retry on network/connection errors only (no GraphQL data means the request failed).
    retryIf: (error) => Boolean(error),
  },
});

const CoreEndpoint = new HttpLink({
  uri: process.env.NODE_ENV === "development" ? process.env.GATSBY_API_CORE : "https://api.swapcard.com/graphql",
  fetch,
});

const StudioEndpoint = new HttpLink({
  uri: process.env.NODE_ENV === "development" ? process.env.GATSBY_API_STUDIO : "https://studio-api.swapcard.com/graphql",
  fetch,
});

const DeveloperEndpoint = new HttpLink({
  uri: "https://developer.swapcard.com/event-admin/graphql",
  headers: {
    Authorization: process.env.ENTIRELY_API_KEY!,
  },
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: retryLink.concat(
    ApolloLink.split(
      (operation) => operation.getContext().clientName === "developer",
      DeveloperEndpoint,
      ApolloLink.split((operation) => operation.getContext().clientName === "studio", StudioEndpoint, CoreEndpoint)
    )
  ),
});

function headerMiddleware(headers: any) {
  return new ApolloLink((operation, forward) => {
    operation.setContext(({ headers: existingHeaders = {} }) => ({
      headers: {
        ...existingHeaders,
        ...headers,
      },
    }));

    return forward(operation);
  });
}

export function setClientHeaders(headers: Record<string, any>) {
  const middleware = headerMiddleware(headers);

  const newCoreEndpoint = middleware.concat(CoreEndpoint);
  const newStudioEndpoint = middleware.concat(StudioEndpoint);

  client.setLink(
    retryLink.concat(
      ApolloLink.split(
        (operation) => operation.getContext().clientName === "developer",
        DeveloperEndpoint,
        ApolloLink.split((operation) => operation.getContext().clientName === "studio", newStudioEndpoint, newCoreEndpoint)
      )
    )
  );
}

export default client;
